/**
 * ИНДИКАТОРЫ НАБОРА (typing notifications), Антон 2026-09-16 21:27 UTC.
 *
 * ДВЕ СТОРОНЫ:
 *
 *  а) ОТПРАВКА — `useTypingSender(client, roomId)` возвращает функции
 *     для композера. Протокол (Matrix C-S API, PUT
 *     /_matrix/client/v3/rooms/{roomId}/typing/{userId}):
 *       - пока человек печатает: { typing: true, timeout: 30000 } не чаще
 *         раза в ~10 с — иначе каждый символ бил бы в /sync всех собеседников
 *         и в rate limit rc_message-подобных ручьёв Synapse;
 *       - сообщение отправлено ИЛИ поле очищено: { typing: false }.
 *     Сетевой вызов — `MatrixClient.sendTyping(roomId, isTyping, timeoutMs)`
 *     (проверено по node_modules/matrix-js-sdk/lib/client.d.ts 42.3.0).
 *     Ошибки глотаем молча: индикатор набора — второстепенная фича, а не
 *     действие пользователя; показывать красную ошибку «не удалось
 *     сообщить, что вы печатаете» было бы абсурдом.
 *
 *  б) ОТОБРАЖЕНИЕ — `useTypingNames(client, room)` возвращает имена тех,
 *     кто СЕЙЧАС печатает (без самого себя). Источник — ephemeral-событие
 *     m.typing из /sync: sync.js кладёт его в `room.addEphemeralEvents`,
 *     room-state обновляет `RoomMember.typing`, и MatrixClient пробрасывает
 *     событие `RoomMember.typing` (проверено по исходникам 42.3.0:
 *     reEmit(member, [RoomMemberEvent.Name, RoomMemberEvent.Typing, ...])
 *     в sync.js `_createAndReEmitRoom`). Сервер сам снимает флаг по
 *     таймауту, поэтому зависших «печатает…» быть не должно.
 *
 * Typing ОБЩИЙ на комнату: протокол не различает композер треда и комнаты,
 * поэтому индикатор рисуется одинаково в обоих местах.
 */
import { useEffect, useRef, useState } from "react";
import {
  RoomMemberEvent,
  type MatrixClient,
  type MatrixEvent,
  type Room,
  type RoomMember,
} from "matrix-js-sdk";

/** Таймаут, который просим у сервера для typing:true (мс). */
const TYPING_TIMEOUT_MS = 30_000;
/**
 * Минимальный интервал между typing:true (мс). Сервер продлевает флаг на
 * TYPING_TIMEOUT_MS, так что 10 с — трёхкратный запас: даже потерянный
 * запрос успеет повториться до истечения таймаута.
 */
const TYPING_RESEND_MS = 10_000;

export interface TypingSender {
  /**
   * Композер сообщает: текст изменился. Пустая строка = поле очищено
   * (typing:false); непустая — человек печатает (typing:true с троттлингом).
   */
  noteText: (text: string) => void;
  /**
   * Сообщение отправлено (или композер размонтируется): гарантированно
   * погасить индикатор у собеседников.
   */
  stop: () => void;
}

/**
 * Отправка typing-уведомлений из композера.
 *
 * Реализация — ref-based, а не state: typing-статусы не должны вызывать
 * ререндер композера на каждый символ.
 */
export function useTypingSender(
  client: MatrixClient | null,
  roomId: string,
): TypingSender {
  /** Момент последнего отправленного typing:true (0 — не отправляли). */
  const lastSentAt = useRef(0);
  /** true, если по последнему нашему уведомлению мы «печатаем». */
  const flagged = useRef(false);
  // Свежие значения для колбэков, чтобы в замыканиях не было протухших
  // client/roomId при смене комнаты.
  const clientRef = useRef(client);
  const roomIdRef = useRef(roomId);
  clientRef.current = client;
  roomIdRef.current = roomId;

  const send = (isTyping: boolean) => {
    const cli = clientRef.current;
    if (!cli) return;
    if (isTyping) {
      lastSentAt.current = Date.now();
      flagged.current = true;
    } else {
      flagged.current = false;
      lastSentAt.current = 0;
    }
    // timeoutMs обязателен по сигнатуре SDK; для typing:false он не
    // используется сервером, передаём 0.
    void cli
      .sendTyping(roomIdRef.current, isTyping, isTyping ? TYPING_TIMEOUT_MS : 0)
      .catch(() => {
        /* см. шапку: индикатор набора не повод для ошибки в UI */
      });
  };

  const noteText = (text: string) => {
    if (text.length === 0) {
      // Поле очистили. Если typing:true ушёл недавно, сервер в любом случае
      // погасит флаг по таймауту — но 30 с «печатает…» после очистки поля
      // выглядит сломанным, поэтому шлём typing:false сразу. Если флага не
      // было (поле и так было пустым) — не шлём ничего.
      if (flagged.current) send(false);
      return;
    }
    // Печатает. Первый символ после паузы — сразу typing:true; дальше —
    // не чаще раза в TYPING_RESEND_MS, пока ввод продолжается.
    const now = Date.now();
    if (!flagged.current || now - lastSentAt.current >= TYPING_RESEND_MS) {
      send(true);
    }
  };

  const stop = () => {
    if (flagged.current) send(false);
  };

  // Смена комнаты/размонтирование: не оставляем за собой горящий
  // «печатает…» в прежней комнате. Комнату и клиента берём из ЗАМЫКАНИЯ
  // эффекта, а не из refs: к моменту cleanup refs уже обновлены рендером
  // на новую комнату, и typing:false ушёл бы не туда.
  useEffect(() => {
    const cli = client;
    const rid = roomId;
    return () => {
      if (flagged.current && cli) {
        flagged.current = false;
        void cli.sendTyping(rid, false, 0).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  return { noteText, stop };
}

/**
 * Имена участников комнаты, которые СЕЙЧАС печатают (кроме нас самих).
 *
 * Подписка на MatrixClient: событие `RoomMember.typing` приходит с
 * (event, member); фильтруем по комнате — события чужих комнат нас не
 * касаются. Перечитываем ВЕСЬ список участников на каждое событие: это
 * дешевле, чем поддерживать инкрементальное множество, и не расходится
 * с тем, что сервер считает актуальным.
 */
export function useTypingNames(
  client: MatrixClient | null,
  room: Room | null,
): string[] {
  const [names, setNames] = useState<string[]>([]);

  useEffect(() => {
    if (!client || !room) {
      setNames([]);
      return;
    }
    const me = client.getUserId();
    const update = () => {
      const typing = room
        .getMembers()
        .filter((m) => m.typing && m.userId !== me)
        .map((m) => m.name);
      // setState с новым массивом на КАЖДОЕ событие заставлял бы React
      // перерисовываться даже когда состав не изменился (typing-события
      // приходят часто). Сравниваем содержимое — дёшево, людей в комнате
      // немного.
      setNames((prev) =>
        prev.length === typing.length && prev.every((n, i) => n === typing[i])
          ? prev
          : typing,
      );
    };
    update();

    const onTyping = (_event: MatrixEvent, member: RoomMember) => {
      if (member.roomId !== room.roomId) return;
      update();
    };
    client.on(RoomMemberEvent.Typing, onTyping);
    return () => {
      client.off(RoomMemberEvent.Typing, onTyping);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, room?.roomId]);

  return names;
}
